'use client';

import { useState, useEffect } from 'react';
import { HorizontalCarousel } from './HorizontalCarousel';

const ACCENT = '#3d8b7a';

export interface EventItem {
  Event_ID: number;
  Event_Title: string;
  Ministry_ID: number;
  Image_URL: string | null;
}

export interface EventsCarouselProps {
  /** Base URL for the API (the host serving /api/events) */
  apiBaseUrl?: string;
  /** Campus filter — accepts a Campus_Slug or a numeric Congregation_ID */
  campus?: string;
  /** Ministry ID filter */
  ministryId?: number;
  /** Program ID filter — narrower than a ministry (e.g. Celebrate Recovery) */
  programId?: number;
  /** Key-word filter, matched against event title, program name, ministry name */
  search?: string;
  /**
   * @deprecated The in-widget "View All" button was removed; the host page
   * supplies its own button with prefiltered URL params. Accepted but ignored.
   */
  viewAllUrl?: string;
}

const MOCK_EVENTS: EventItem[] = [
  { Event_ID: 1, Event_Title: 'Cedar Point Trip', Ministry_ID: 1, Image_URL: null },
  { Event_ID: 2, Event_Title: 'Parent Child Dedication Class', Ministry_ID: 2, Image_URL: null },
  { Event_ID: 3, Event_Title: 'Camp Woodside 2025', Ministry_ID: 3, Image_URL: null },
  { Event_ID: 4, Event_Title: 'Next Steps', Ministry_ID: 4, Image_URL: null },
  { Event_ID: 5, Event_Title: 'Vacation Bible School', Ministry_ID: 1, Image_URL: null },
];

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_URL = process.env.CHURCH_HUB_API_URL ?? '';
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

export function EventsCarousel({
  apiBaseUrl = BAKED_API_URL,
  campus,
  ministryId,
  programId,
  search,
}: EventsCarouselProps) {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchEvents() {
      try {
        const params = new URLSearchParams();
        if (campus) params.set('campus', campus);
        if (ministryId) params.set('ministry_id', String(ministryId));
        if (programId) params.set('program_id', String(programId));
        if (search) params.set('search', search);
        const qs = params.toString();

        const headers: Record<string, string> = {};
        if (BAKED_API_KEY) headers['x-api-key'] = BAKED_API_KEY;
        if (BAKED_APP_SLUG) headers['x-app-slug'] = BAKED_APP_SLUG;
        const res = await fetch(`${apiBaseUrl}/api/events${qs ? `?${qs}` : ''}`, { headers });
        if (!res.ok) throw new Error(`${res.status}`);
        const data: EventItem[] = await res.json();

        // An empty list is a real answer, not a misconfiguration — a filtered
        // embed (ministry/program/search) can legitimately have nothing coming
        // up, and showing sample events there would publish fake events.
        if (!cancelled) setEvents(data);
      } catch {
        if (!cancelled) {
          setEvents(MOCK_EVENTS);
          setUsingMock(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, campus, ministryId, programId, search]);

  // Nothing upcoming for this filter — render nothing rather than an empty rail.
  if (!isLoading && events.length === 0) return null;

  return (
    <section>
      {/* Header */}
      <div className="mb-8">
        <p className="text-sm font-bold tracking-[0.2em] uppercase" style={{ color: ACCENT }}>
          Upcoming
        </p>
        <h2 className="mt-1 text-4xl leading-tight font-bold">Events</h2>
      </div>

      {/* Carousel */}
      <HorizontalCarousel showProgress accentColor={ACCENT} fadeBg="#ffffff">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="w-[300px] flex-shrink-0 sm:w-[380px] lg:w-[460px]">
                <div className="aspect-video animate-pulse bg-gray-200" />
                <div className="mt-4 h-4 w-48 animate-pulse rounded bg-gray-200" />
              </div>
            ))
          : events.map((event) => (
              <a
                key={event.Event_ID}
                href={`https://woodsidebible.org/events/details/?id=${event.Event_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-[300px] flex-shrink-0 text-inherit no-underline sm:w-[380px] lg:w-[460px]"
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  {event.Image_URL ? (
                    <img
                      src={event.Image_URL}
                      alt={event.Event_Title}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 px-4 text-center text-sm font-medium text-gray-400">
                      {event.Event_Title}
                    </div>
                  )}
                </div>
                <h3 className="mt-4 text-sm font-semibold tracking-wider uppercase">
                  {event.Event_Title}
                </h3>
              </a>
            ))}
      </HorizontalCarousel>

      {usingMock && (
        <p className="mt-2 text-center text-xs text-gray-400">
          Sample data — configure API to show live events
        </p>
      )}
    </section>
  );
}
